import React, { useState } from 'react';
import styles from './EditUserNameModal.module.css';
import {FloComponentProps, FloDropdown} from 'components';
import { FloModal } from 'components';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Message from 'containers/common/Message';
import { Form, Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FloFormGroup, FloModalBodyProps, FloFormControl } from 'components';
import { User, Credentials } from 'model';
import { FloButton, FloButtonType } from 'components/FloButton';
import classnames from 'classnames';
import { PhoneNumber } from 'model/PhoneNumber';

// eslint-disable-next-line no-useless-escape
const phoneRegExp = /^(?:(?:\(?(?:00|\+)([1-4]\d\d|[1-9]\d?)\)?)?[\-\.\ \\\/]?)?((?:\(?\d{1,}\)?[\-\.\ \\\/]?){0,})(?:[\-\.\ \\\/]?(?:#|ext\.?|extension|x)[\-\.\ \\\/]?(\d+))?$/i


interface EditUserNameBodyProps extends FloModalBodyProps {
  isLoading?:           boolean;
  currentUser?:         User;
  saveUser:             (user: User, credentials?: Credentials) => void;
  isSubmitting:         boolean;
  authenticatedUserId?: string | null,
}

const EditUserNameBody: React.FC<EditUserNameBodyProps> = React.memo(({
  bindIsFormValid,
  bindSubmitForm,
  isLoading,
  currentUser,
  saveUser,
  isSubmitting,
  authenticatedUserId
}) => {
  const { t } = useTranslation();
  const [passwordHidden, setPasswordHidden] = useState(true);

  const validationData: any = {
    firstName: Yup.string().required(t('validation_required', { field: t('First Name') })),
    lastName: Yup.string().required(t('validation_required', { field: t('Last Name') })),
    email: Yup.string().required(t('validation_required', { field: t('Email') })),
    countryCode: Yup.string().required(t('validation_required', { field: t('Country Code') })),
    phone: Yup.string().matches(phoneRegExp, t('validation_phone_invalid')).required(t('validation_required', { field: t('Phone') })),
  };
  if (!passwordHidden) {
    validationData.oldPassword = Yup.string().required(t('validation_required', { field: t('Old Password') }));
    validationData.newPassword = Yup.string().required(t('validation_required', { field: t('New Password') }))
        .min(8, t('min_password'))
        .matches(/[a-z]/, t('lowercase_required'))
        .matches(/[A-Z]/, t('uppercase_required'))
        .matches(/[0-9]/, t('number_required'))
        .matches(/[!@#$%^&*)(+=._-]/, t('special_char_required'));
    validationData.confirmPassword = Yup.string().when("newPassword", {
      is: val => (val && val.length > 0 ? true : false),
      then: Yup.string().oneOf(
        [Yup.ref("newPassword")], t('password_not_match'))
    }).required(t('validation_required', { field: t('Confirm Password') }));
  }
  const validationSchema = Yup.object().shape(validationData);
  const phoneNumber = PhoneNumber.fromString(currentUser?.phoneMobile || '');
  return (
    <>
      <Message />
      <Formik
        enableReinitialize
        initialValues={{
          firstName: currentUser?.firstName || '',
          lastName: currentUser?.lastName || '',
          email: currentUser?.email || '',
          newPassword: '',
          oldPassword: '',
          confirmPassword: '',
          countryCode: phoneNumber.countryPrefix,
          phone: phoneNumber.phone || '',
        }}
        onSubmit={(values, actions) => {
          saveUser({
            ...values,
            phoneMobile: `${values.countryCode}${values.phone}`,
            id: currentUser?.id
          },
            values.newPassword ? {
              password: values.newPassword,
              username: '',
              oldPassword: values.oldPassword
            } : undefined
          );
          actions.setSubmitting(false);
        }}
        validationSchema={validationSchema}
      >
        {({ values, handleBlur, handleChange, errors, touched, isValid, handleSubmit, submitForm, setFieldValue, setFieldTouched }) => {
          bindSubmitForm(submitForm);
          bindIsFormValid(isValid);

          const setField = (name: string, value: any) => {
            setFieldValue(name, value, true);
            setFieldTouched(name, true, false);
          };
          return (
            <Form className={styles.form} onSubmit={handleSubmit}>
              <Row>
                <Col xs="12">
                  <FloFormGroup
                    controlId   = "firstName"
                    label       = {t('First Name')}
                    touched     = {touched}
                    errors      = {errors}
                  >
                    <FloFormControl
                      type="text"
                      name="firstName"
                      placeholder={t('First Name')}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.firstName}
                      disabled={isLoading || isSubmitting}
                      isInvalid={!!(touched.firstName && errors.firstName)}
                    />
                  </FloFormGroup>
                </Col>
              </Row>

              <Row>
                <Col xs="12">
                  <FloFormGroup
                    controlId   = "lastName"
                    label       = {t('Last Name')}
                    touched     = {touched}
                    errors      = {errors}
                  >
                    <FloFormControl
                      type="text"
                      name="lastName"
                      placeholder={t('Last Name')}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.lastName}
                      disabled={isLoading || isSubmitting}
                      isInvalid={!!(touched.lastName && errors.lastName)}
                    />
                  </FloFormGroup>
                </Col>
              </Row>

              <Row>
                <Col xs="12">
                  <FloFormGroup
                    controlId   = "email"
                    label       = {t('Email')}
                    touched     = {touched}
                    errors      = {errors}
                  >
                    <FloFormControl
                      type="email"
                      name="email"
                      placeholder={t('Email')}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.email}
                      disabled={isLoading || isSubmitting}
                      isInvalid={!!(touched.email && errors.email)}
                    />
                  </FloFormGroup>
                </Col>
              </Row>

              <Row>
                <Col xs="5">
                  <FloFormGroup
                      controlId   = "countryCode"
                      label       = {t('Country Code')}
                      touched     = {touched}
                      errors      = {errors}
                  >
                    <FloDropdown
                        id      = "countryCode"
                        title   = {t('Country Code')}
                        values  = {values.countryCode}
                        full
                        disabled = {isLoading || isSubmitting}
                        options = {PhoneNumber.countryCodes}
                        onChange = {e => setField('countryCode', e.value)}
                        isInvalid={!!(touched.countryCode && errors.countryCode)}
                    />
                  </FloFormGroup>
                </Col>
                <Col xs="7">
                  <FloFormGroup
                      controlId   = "phone"
                      label       = {t('Phone')}
                      touched     = {touched}
                      errors      = {errors}
                  >
                    <FloFormControl
                        type="text"
                        name="phone"
                        placeholder={t('Phone')}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.phone}
                        disabled={isLoading || isSubmitting}
                        isInvalid={!!(touched.phone && errors.phone)}
                    />
                  </FloFormGroup>
                </Col>
              </Row>

              { authenticatedUserId !== currentUser?.id ? null : passwordHidden ?
              <Row className="justify-content-center">
                <Col className={classnames("col-auto", styles.passwordButtonWrapper)}>
                  <FloButton
                    text       = {t('Change Password')}
                    onClick    = {() => { setPasswordHidden(false) }}
                    type       = {FloButtonType.SMALL}
                    showLoader = {false}
                    disabled   = {false}
                  />
                </Col>
              </Row>
              :
                <>
                  <FloFormGroup
                    controlId   = "oldPassword"
                    label       = {t('Old Password')}
                    touched     = {touched}
                    errors      = {errors}
                  >
                    <FloFormControl
                      type="password"
                      name="oldPassword"
                      placeholder={t('Old Password')}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.oldPassword}
                      disabled={isLoading || isSubmitting}
                      isInvalid={!!(touched.oldPassword && errors.oldPassword)}
                    />
                  </FloFormGroup>
                  <FloFormGroup
                    controlId   = "newPassword"
                    label       = {t('New Password')}
                    touched     = {touched}
                    errors      = {errors}
                  >
                    <FloFormControl
                      type="password"
                      name="newPassword"
                      placeholder={t('New Password')}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.newPassword}
                      disabled={isLoading || isSubmitting}
                      isInvalid={!!(touched.newPassword && errors.newPassword)}
                    />
                  </FloFormGroup>
                  <FloFormGroup
                    controlId   = "confirmPassword"
                    label       = {t('Confirm Password')}
                    touched     = {touched}
                    errors      = {errors}
                  >
                    <FloFormControl
                      type="password"
                      name="confirmPassword"
                      placeholder={t('Confirm Password')}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.confirmPassword}
                      disabled={isLoading || isSubmitting}
                      isInvalid={!!(touched.confirmPassword && errors.confirmPassword)}
                    />
                  </FloFormGroup>
                </>
              }
            </Form>
          )
        }}
      </Formik>
    </>
  )
})

export interface EditUserNameModalProps extends FloComponentProps {
  show:                 boolean;
  onHide:               () => void;
  isLoading:            boolean;
  currentUser?:         User;
  saveUser:             (user: User, credentials?: Credentials) => void;
  isSubmitting:         boolean;
  authenticatedUserId?: string | null,
}

export const EditUserNameModal: React.FC<EditUserNameModalProps> = React.memo(({
  isLoading,
  currentUser,
  saveUser,
  authenticatedUserId,
  isSubmitting,
  ...rest
}) => {
  const { t } = useTranslation();
  return (
    <FloModal
      {...rest}
      name           = "edit-user-name"
      title          = {t('Edit Name/Email/Password')}
      isSubmitting   = {isSubmitting}
      disableButtons = {isSubmitting}
      body           = { (props) => <EditUserNameBody
                         {...props}
                         isLoading            = {isLoading}
                         currentUser          = {currentUser}
                         saveUser             = {saveUser}
                         isSubmitting         = {isSubmitting}
                         authenticatedUserId  = {authenticatedUserId}
                      />}
    />
  );
})

export default EditUserNameModal;
